import useLocalStorage from 'core/hooks/utils/useLocalStorage'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useQueryCallbacks } from 'core/hooks/data/useQueryCallbacks'
import APIService from 'api/APIService'
import { JobStatusResponse, OnboardingInvitePayload } from 'types/entity'
import Button from 'core/components/buttons/Button'
import useCurrentTeam from 'core/hooks/team/useCurrentTeam'
import { useSettings } from 'core/hooks/settings/useSettings'
import useNotifications from 'core/hooks/ui/useNotifications'
import { downloadFile } from 'utils/AppHelper'

interface BulkInviteProps {
  onSuccess?: VoidFunction
}

const useBatchDeleteInvite = ({ onSuccess }: BulkInviteProps = {}) => {
  const queryClient = useQueryClient()
  const { data: currentTeam } = useCurrentTeam()
  const { data: settings, set } = useSettings()
  const notification = useNotifications()

  const [localJobId, setJobId] = useLocalStorage(
    'member_batch_delete_invite_job_id',
    ''
  )
  const jobID = settings.pollingJobs.memberBatchDeleteInviteJobId || localJobId

  useQueryCallbacks({
    queryKey: ['member-batch-delete-invite-polling', jobID],
    queryFn: () =>
      APIService.misc.getJobStatus({
        jobId: jobID
      }),
    enabled: !!jobID,
    refetchInterval: 2000,
    onSuccess: ({
      status,
      data: {
        archived_invitees_count,
        total_count,
        file_url,
        partial_success,
        skipped_invitees_count
      }
    }: JobStatusResponse<{
      file_url: string
      skipped_invitees_count: number
      archived_invitees_count: number
      total_count: number
      partial_success: boolean
      error: {
        message: string //comma separated list of errors
        errors: string[]
      }
    }>) => {
      if (status === 'finished') {
        queryClient.invalidateQueries({ queryKey: ['members'] })
        queryClient.invalidateQueries({ queryKey: ['onboarding'] })

        notification.dismiss('memberBatchDeleteInviteJobId')
        setJobId('')
        set('pollingJobs', {
          ...settings.pollingJobs,
          memberBatchDeleteInviteJobId: ''
        })

        const downloadButton = file_url ? (
          <Button
            className="text-primary"
            variant="link"
            onClick={() => {
              downloadFile(file_url, 'errors_list')
            }}
          >
            Download list of errors
          </Button>
        ) : null

        // All success
        if (archived_invitees_count === total_count) {
          notification.success({
            title: 'Member(s) invite deleted',
            id: jobID
          })
        }

        // All invites failed
        if (!partial_success && skipped_invitees_count === total_count) {
          notification.error({
            title: `Failed to delete all ${total_count} invites`,
            subtitle: downloadButton
              ? `Download the list of errors to see which invites were unable to delete.`
              : '',
            id: jobID,
            footer: () => {
              return (
                <div className="flex items-center gap-3">{downloadButton}</div>
              )
            }
          })
        }

        // Some success and some others failed
        if (
          archived_invitees_count &&
          skipped_invitees_count &&
          partial_success
        ) {
          notification.error({
            title: `Failed to delete invite to ${archived_invitees_count}/${total_count} member`,
            subtitle: downloadButton
              ? `Download the list of errors to see which invites were unable to delete.`
              : '',
            id: jobID,
            footer: () => {
              return (
                <div className="flex items-center gap-3">{downloadButton}</div>
              )
            }
          })
        }
      }

      if (status === 'failed') {
        notification.dismiss('memberBatchDeleteInviteJobId')
        setJobId('')
        set('pollingJobs', {
          ...settings.pollingJobs,
          memberBatchDeleteInviteJobId: ''
        })
        notification.error({
          title: 'Could not delete invites',
          id: jobID
        })
      }
    },
    onError: () => {
      notification.dismiss('memberBatchDeleteInviteJobId')
      notification.error({
        title: 'Could not delete invites',
        id: jobID
      })
      setJobId('')
      set('pollingJobs', {
        ...settings.pollingJobs,
        memberBatchDeleteInviteJobId: ''
      })
    }
  })

  const deleteInviteQuery = useMutation({
    mutationKey: ['delete', 'batch', 'invite', 'onboarding'],
    mutationFn: (payload: OnboardingInvitePayload) => {
      return APIService.members.deleteMemberInvite({
        payload,
        teamId: currentTeam?.token_id ?? ''
      })
    },
    onSuccess: (data) => {
      notification.loading({
        title: 'Deleting invites...',
        subtitle:
          'This may take a few minutes. You can close this pop up and we’ll keep working in the background.',
        duration: Infinity,
        id: 'memberBatchDeleteInviteJobId'
      })
      setTimeout(() => {
        set('pollingJobs', {
          ...settings.pollingJobs,
          memberBatchDeleteInviteJobId: data.token_id
        })
      }, 0)
      setJobId(data.token_id)
      onSuccess?.()
    },
    onError: () => {
      notification.error('An error has occurred trying delete invites')
    }
  })

  return {
    deleteInviteQuery
  }
}

export default useBatchDeleteInvite
